import { create } from 'zustand';
import type { WorkItem, CreateWorkItemInput, UpdateWorkItemInput, WorkItemStatus } from '@/types/workItem';
import {
  listWorkItems,
  createWorkItem,
  updateWorkItem,
  deleteWorkItem,
} from '@/services/workItemService';

interface WorkItemsState {
  workItems: WorkItem[];
  isLoading: boolean;
  error: string | null;

  // Actions
  loadWorkItems: (params?: { channelId?: string; ownerUserId?: string; status?: WorkItemStatus }) => Promise<void>;
  addWorkItem: (input: CreateWorkItemInput) => Promise<WorkItem>;
  editWorkItem: (id: string, input: UpdateWorkItemInput) => Promise<void>;
  removeWorkItem: (id: string) => Promise<void>;

  // SignalR handlers
  handleWorkItemCreated: (item: WorkItem) => void;
  handleWorkItemUpdated: (item: WorkItem) => void;
  handleWorkItemStatusChanged: (item: WorkItem) => void;
  handleWorkItemDeleted: (itemId: string) => void;

  reset: () => void;
}

function upsertWorkItem(items: WorkItem[], item: WorkItem): WorkItem[] {
  if (items.some(existing => existing.id === item.id)) {
    return items.map(existing => (existing.id === item.id ? item : existing));
  }

  return [item, ...items];
}

export const useWorkItemsStore = create<WorkItemsState>((set) => ({
  workItems: [],
  isLoading: false,
  error: null,

  loadWorkItems: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const workItems = await listWorkItems(params);
      set({ workItems, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  addWorkItem: async (input) => {
    const item = await createWorkItem(input);
    set((s) => ({
      workItems: [item, ...s.workItems.filter((wi) => wi.id !== item.id)],
    }));
    return item;
  },

  editWorkItem: async (id, input) => {
    const updated = await updateWorkItem(id, input);
    set((s) => ({
      workItems: s.workItems.map((wi) => (wi.id === id ? updated : wi)),
    }));
  },

  removeWorkItem: async (id) => {
    await deleteWorkItem(id);
    set((s) => ({ workItems: s.workItems.filter((wi) => wi.id !== id) }));
  },

  handleWorkItemCreated: (item) => {
    set((s) => {
      return { workItems: upsertWorkItem(s.workItems, item) };
    });
  },

  handleWorkItemUpdated: (item) => {
    set((s) => ({
      workItems: upsertWorkItem(s.workItems, item),
    }));
  },

  handleWorkItemStatusChanged: (item) => {
    set((s) => ({
      workItems: upsertWorkItem(s.workItems, item),
    }));
  },

  handleWorkItemDeleted: (itemId) => {
    set((s) => ({
      workItems: s.workItems.filter((wi) => wi.id !== itemId),
    }));
  },

  reset: () => set({ workItems: [], isLoading: false, error: null }),
}));
